import { useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { Dropdown, Header, Label } from "semantic-ui-react";

import TagService from "services/Tag";
import { basicBorder, getTextColor, getYiq } from "helpers/tags";
import ApplyTagModal from "components/modals/ApplyTagModal";

const getTagLabelStyle = tag => {
  const bgColor = tag.color;
  const textColor = getTextColor(bgColor);
  const border = getYiq(bgColor) >= 225 ? basicBorder : undefined;
  return {
    background: bgColor,
    color: textColor,
    boxShadow: "none",
    border,
    position: "relative",
  };
};

const fetchTags = async ({ queryKey = [] }) => {
  const [, modelType] = queryKey;
  if (!modelType) {
    throw new Error("Tag ModelType is required");
  }
  return await TagService.getTags({ model: modelType });
};

export function TagFormField({ modelType, formValue = [], onChange }) {
  const applyTagModalRef = useRef();
  const { data: tagOptions } = useQuery({
    queryKey: ["tags", modelType],
    queryFn: fetchTags,
    enabled: !!modelType,
    initialData: [],
  });

  // Forms only manage enabled tags. Disabled tags attached to a record are
  // preserved on save by the backend but never surfaced here.
  const parsedOptions = tagOptions
    .filter(tag => tag.enabled)
    .map(tag => ({
      key: `tag-item-${tag.id}`,
      text: tag.name,
      value: tag.id,
      tag: tag,
      content: <Label content={tag.name} style={getTagLabelStyle(tag)} />,
    }));
  const parsedValues = formValue.filter(tag => tag.enabled).map(tag => tag.id);

  const handleTagChange = (e, { value = [], options = [] }) => {
    const knownIds = new Set(options.map(op => op.value));
    const hiddenTags = formValue.filter(t => !knownIds.has(t.id));
    // A tag already selected keeps its record, so metadata values entered
    // before saving survive adding or removing another tag.
    const selectedById = new Map(formValue.map(t => [t.id, t]));
    const tags = options
      .filter(op => value.includes(op.value))
      .map(op => selectedById.get(op.value) ?? op.tag);
    onChange([...tags, ...hiddenTags]);
  };

  const renderLabel = op => {
    const attachedTag = formValue.find(t => t.id === op.value) || op.tag;
    const hasMetadata = (attachedTag?.tag_metadata_fields || []).length > 0;

    return {
      style: getTagLabelStyle(op.tag),
      content: (
        <>
          {hasMetadata && (
            <span
              title="Has metadata"
              style={{
                position: "absolute",
                top: -4,
                left: -4,
                width: 16,
                height: 16,
                borderRadius: "50%",
                backgroundColor: "#FF6B35",
                color: "#ffffff",
                fontSize: 10,
                fontWeight: 600,
                lineHeight: "16px",
                textAlign: "center",
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.15)",
                pointerEvents: "none",
              }}
            >
              M
            </span>
          )}
          {op.text}
        </>
      ),
      onClick: () => applyTagModalRef.current.open(attachedTag),
    };
  };

  const handleMetadataChange = (tag, metadata) => {
    const tag_metadata_values = metadata.map(f => ({
      field_id: f.id,
      field_name: f.field_name,
      value: f.value,
    }));
    const updatedTag = { ...tag, tag_metadata_values };
    const next = formValue.some(t => t.id === tag.id)
      ? formValue.map(t => (t.id === tag.id ? updatedTag : t))
      : [...formValue, updatedTag];
    onChange(next);
  };

  return (
    <div>
      <Header className="section-header ui form sub-header">Tags</Header>
      <Dropdown
        options={parsedOptions}
        value={parsedValues}
        onChange={handleTagChange}
        fluid
        multiple
        search
        selection
        renderLabel={renderLabel}
      />
      <ApplyTagModal ref={applyTagModalRef} onApplyTag={handleMetadataChange} />
    </div>
  );
}
